import { CheckboxBlockField } from '@app/form';
import { t } from 'i18next';
import { CustomSelectChoice, FieldAnswerChoices, FieldChoice } from '@app/api';
import React, { ChangeEvent, useCallback } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';

const useStyles = createUseStyles((theme: Theme) => ({
  checkbox: {
    marginTop: theme.marginBase * 2,
    backgroundColor: theme.colors.white,
  },
}));

interface Props {
  choice: FieldChoice;
  index: number;
  choices?: FieldAnswerChoices;
  setFieldValue?: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) => void;
}

export const MultipleChoiceChoice = ({
  choice,
  index,
  choices,
  setFieldValue,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const otherField = choice.ref === CustomSelectChoice.Other;

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!choices || !setFieldValue) {
        return;
      }
      if (e.currentTarget.checked) {
        setFieldValue('choices', {
          refs: [...choices.refs, choice.ref],
          labels: [...choices.labels, choice.label],
          values: [...(choices.values || []), choice.value || choice.ref],
          metadata: [...(choices.metadata || []), choice.metadata || {}],
        });
      } else {
        const index = choices.refs.indexOf(choice.ref);
        const refs = [...choices.refs];
        const labels = [...choices.labels];
        const choiceValues = [...(choices.values || [])];
        const metadata = [...(choices.metadata || [])];
        refs.splice(index, 1);
        labels.splice(index, 1);
        choiceValues.splice(index, 1);
        metadata.splice(index, 1);

        setFieldValue('choices', {
          refs,
          labels,
          values: choiceValues,
          metadata,
        });
      }
    },
    [choice.label, choice.ref, choice.value, choices, setFieldValue],
  );

  const onChangeValueOther = (value: string) => {
    if (!choices || !setFieldValue) {
      return;
    }
    const index = choices.refs.indexOf('other');
    const newLabels = [...choices.labels];
    newLabels[index] = value;
    setFieldValue('choices', {
      refs: [...choices.refs],
      labels: [...newLabels],
      values: [...(choices.values || [])],
    });
  };

  return (
    <CheckboxBlockField
      key={choice.ref}
      className={classes.checkbox}
      label={choice.label || t('screeningForm.noLabel', { index: index + 1 })}
      name={otherField ? 'other' : undefined}
      editable={otherField}
      otherPlaceholder={t('screeningForm.otherPlaceholder')}
      checked={choices && choices.refs.includes(choice.ref)}
      onChange={onChange}
      onEditValueChange={otherField ? onChangeValueOther : undefined}
      icon={choice?.icon}
    />
  );
};
